import type { CSSProperties, ReactNode } from 'react';
import { Children } from 'react';

export interface CardListProps {
  children: ReactNode;
  hairlines?: boolean;
  /** Drop the border, radius, and background — for a list already sitting inside a Surface. */
  flush?: boolean;
}

export function CardList({ children, hairlines = true, flush }: CardListProps) {
  return (
    <div
      style={{
        background: flush ? 'transparent' : 'var(--surface-raised)',
        border: flush ? 'none' : '1px solid var(--border)',
        borderRadius: flush ? 0 : 12,
        overflow: 'hidden',
      }}
    >
      {Children.map(children, (c, i) => (
        <div style={{ borderTop: hairlines && i > 0 ? '1px solid var(--border-subtle)' : 'none' }}>{c}</div>
      ))}
    </div>
  );
}

export interface CardListRowProps {
  primary: ReactNode;
  secondary?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  meta?: ReactNode;
  onClick?: () => void;
  accent?: string;
  dense?: boolean;
}

export function CardListRow({
  primary,
  secondary,
  leading,
  trailing,
  meta,
  onClick,
  accent,
  dense,
}: CardListRowProps) {
  const primaryStyle: CSSProperties = {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.25,
    color: accent || 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        border: 0,
        padding: dense ? '10px 14px' : '14px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        minHeight: 56,
        color: 'inherit',
      }}
    >
      {leading && <div style={{ flex: '0 0 auto' }}>{leading}</div>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={primaryStyle}>{primary}</div>
        {secondary && (
          <div
            className="ri-small"
            style={{
              marginTop: 2,
              fontSize: 12.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {secondary}
          </div>
        )}
        {meta}
      </div>
      {trailing && (
        <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 6 }}>{trailing}</div>
      )}
    </button>
  );
}
